/**
 * Task Title Generation Helpers
 * Auto-generates task titles based on task type, category, and subcategory
 */

/**
 * Format ID to readable string (e.g., 'utilities-bills' -> 'Utilities Bills')
 */
function formatIdToReadable(id) {
  if (!id) return '';
  return id
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Build an auto-generated task title
 * @param {Object} taskData - Task data containing type, category, subcategory
 * @returns {string} - Generated title
 */
export function buildAutoTaskTitle(taskData) {
  const { taskType, category, subcategory, subcategoryName } = taskData;

  // Fallback if missing required fields
  if (!category || !subcategory) {
    return '';
  }
  
  // Use subcategoryName if provided, otherwise format the ID
  const displaySubcategory = subcategoryName || formatIdToReadable(subcategory);

  // Financial task titles
  if (taskType === 'financial') {
    // Bills & utilities (handle both old and new category IDs)
    if (category === 'bills-utilities' || category === 'utilities-bills') {
      return `Pay ${displaySubcategory} bill`;
    }
    
    // Savings & investments
    if (category === 'savings-investments') {
      return `Make ${displaySubcategory} contribution`;
    }
    
    // Loan & debt
    if (category === 'loan-debt' || category === 'debt-loans') {
      return `Pay ${displaySubcategory}`;
    }
    
    // Income
    if (category === 'income-earnings' || category === 'income') {
      return `Record ${displaySubcategory}`;
    }
    
    // Insurance
    if (category === 'insurance') {
      return `Pay ${displaySubcategory} premium`;
    }
    
    // Rent/Housing
    if (category === 'rent-housing' || category === 'housing-rent') {
      return `Pay ${displaySubcategory}`;
    }
    
    // Taxes
    if (category === 'tax-compliance' || category === 'taxes') {
      return `File ${displaySubcategory}`;
    }
    
    // Medical/Health
    if (category === 'medical-health' || category === 'healthcare') {
      return `Pay for ${displaySubcategory}`;
    }
    
    // Education
    if (category === 'education') {
      return `Pay ${displaySubcategory} fees`;
    }
    
    // Transportation
    if (category === 'transportation') {
      return `Pay for ${displaySubcategory}`;
    }
    
    // Subscriptions
    if (category === 'subscriptions' || category === 'subscription') {
      return `Pay ${displaySubcategory} subscription`;
    }
    
    // Default financial format
    return `Pay ${displaySubcategory}`;
  }

  // Reminder task titles
  if (taskType === 'reminder') {
    // Check/Review for most reminders (handle both old and new category IDs)
    if (category === 'bills-utilities' || category === 'utilities-bills' || 
        category === 'insurance' || category === 'tax-compliance' || category === 'taxes') {
      return `Review ${displaySubcategory}`;
    }
    
    if (category === 'medical-health' || category === 'healthcare') {
      return `Book ${displaySubcategory} appointment`;
    }
    
    if (category === 'subscription' || category === 'subscriptions') {
      return `Review ${displaySubcategory} subscription`;
    }
    
    // Default reminder format
    return `Reminder: ${displaySubcategory}`;
  }

  // Regular/Personal task titles - just use the subcategory name
  if (taskType === 'personal' || taskType === 'regular') {
    return displaySubcategory;
  }

  // Ultimate fallback: Use formatted subcategory as the title
  return displaySubcategory;
}

/**
 * Check if a title appears to be auto-generated (not manually edited)
 * @param {string} title - Current title
 * @param {Object} taskData - Task data to generate comparison title
 * @returns {boolean} - True if title matches auto-generated format
 */
export function isAutoGeneratedTitle(title, taskData) {
  if (!title || !taskData) return false;
  
  const autoTitle = buildAutoTaskTitle(taskData);
  return title === autoTitle;
}
